import PptxGenJS from 'pptxgenjs';
import { UT_TYPES } from '../constants';
import { AnalysisRow, UtType } from '../types';
import { calcTotals } from './analyze';
import { fmt } from './excelUtils';

/**
 * @param utTypes 산출할 UT 항목 — 화면과 같은 목록·순서를 쓰려고 호출한 쪽에서 넘긴다
 *   (공통코드 MECH_UT_CTGR_CD 기준). 넘기지 않으면 상수 전체를 쓴다.
 */
export async function exportPpt(
  rows: AnalysisRow[],
  versionLabels: string[],
  utTypes: UtType[] = UT_TYPES
): Promise<void> {
  const pptx = new PptxGenJS();
  pptx.layout = 'LAYOUT_WIDE';

  const n = versionLabels.length;
  const s1 = versionLabels[0] || 'V1';
  const s2 = versionLabels[n - 1] || `V${n}`;

  // ── 공통 데이터 계산 (버전별 총량, 차이 = 마지막-첫번째) ──
  const totals = calcTotals(rows, n);
  const diffMachines = rows.filter((r) => r.machineHasDiff).length;

  const changeList = utTypes
    .map((ut) => {
      const arr = totals[ut.key];
      const v1 = arr[0];
      const v2 = arr[arr.length - 1];
      const diff = v2 - v1;
      const pct = v1 === 0 ? null : (diff / v1) * 100;
      return { ut, diff, pct };
    })
    .filter((x) => x.diff !== 0)
    .sort((a, b) => Math.abs(b.diff) - Math.abs(a.diff));

  const incList = changeList.filter((x) => x.diff > 0);
  const decList = changeList.filter((x) => x.diff < 0);

  // ══════════════════════════════════════════════
  // Slide 1: Executive Summary (McKinsey Style)
  // ══════════════════════════════════════════════
  const slide1 = pptx.addSlide();
  slide1.background = { color: 'FFFFFF' };

  slide1.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 0,
    w: 13.33,
    h: 1.1,
    fill: { color: '00205B' },
  });
  slide1.addText('UT Utility Consumption Variance Analysis', {
    x: 0.4,
    y: 0.12,
    w: 10,
    h: 0.5,
    fontSize: 20,
    bold: true,
    color: 'FFFFFF',
    fontFace: 'Arial',
  });
  slide1.addText(`${versionLabels.join(' vs ')}  |  Executive Summary`, {
    x: 0.4,
    y: 0.6,
    w: 10,
    h: 0.35,
    fontSize: 11,
    color: 'A0B4D0',
    fontFace: 'Arial',
  });
  slide1.addText(new Date().toLocaleDateString('ko-KR'), {
    x: 10.5,
    y: 0.4,
    w: 2.4,
    h: 0.35,
    fontSize: 10,
    color: 'A0B4D0',
    fontFace: 'Arial',
    align: 'right',
  });

  // 좌측 KPI 박스 3개
  const kpiData = [
    { label: '전체 Machine', value: `${rows.length}대`, sub: 'Total Machines' },
    { label: '차이 발생', value: `${diffMachines}대`, sub: 'Machines with Delta' },
    { label: '변동 UT 항목', value: `${changeList.length}개`, sub: 'UT Types Changed' },
  ];
  kpiData.forEach((k, i) => {
    const x = 0.3 + i * 2.0;
    slide1.addShape(pptx.ShapeType.rect, {
      x,
      y: 1.25,
      w: 1.8,
      h: 1.1,
      fill: { color: 'F4F6F9' },
      line: { color: 'D1DCE8', pt: 1 },
    });
    slide1.addText(k.value, {
      x,
      y: 1.3,
      w: 1.8,
      h: 0.5,
      fontSize: 22,
      bold: true,
      color: '00205B',
      align: 'center',
      fontFace: 'Arial',
    });
    slide1.addText(k.label, {
      x,
      y: 1.78,
      w: 1.8,
      h: 0.3,
      fontSize: 9,
      color: '64748B',
      align: 'center',
      fontFace: 'Arial',
    });
    slide1.addText(k.sub, {
      x,
      y: 2.05,
      w: 1.8,
      h: 0.25,
      fontSize: 8,
      color: '94A3B8',
      align: 'center',
      fontFace: 'Arial',
    });
  });

  // 우측 인사이트 박스
  let insight = '';
  if (incList.length > 0) {
    insight += `▲ ${incList[0].ut.key}: +${fmt(incList[0].diff)} (${incList[0].pct?.toFixed(
      1
    )}% 증가)\n`;
  }
  if (decList.length > 0) {
    insight += `▼ ${decList[0].ut.key}: ${fmt(decList[0].diff)} (${Math.abs(
      decList[0].pct ?? 0
    ).toFixed(1)}% 감소)\n`;
  }
  insight += `→ 변동폭 상위 항목 원인 분석 권고`;

  slide1.addShape(pptx.ShapeType.rect, {
    x: 6.3,
    y: 1.25,
    w: 6.6,
    h: 1.1,
    fill: { color: 'EFF6FF' },
    line: { color: '93C5FD', pt: 1 },
  });
  slide1.addText('Key Insights', {
    x: 6.5,
    y: 1.3,
    w: 6.2,
    h: 0.3,
    fontSize: 10,
    bold: true,
    color: '1D4ED8',
    fontFace: 'Arial',
  });
  slide1.addText(insight, {
    x: 6.5,
    y: 1.6,
    w: 6.2,
    h: 0.7,
    fontSize: 9.5,
    color: '1e293b',
    fontFace: 'Malgun Gothic',
    lineSpacing: 16,
  });

  // 메인 테이블 (버전 N개 동적 컬럼)
  const headerOpts = {
    bold: true,
    color: 'FFFFFF',
    fill: { color: '00205B' },
    align: 'center' as const,
    fontSize: 10,
  };
  const tableData: PptxGenJS.TableRow[] = [
    [
      { text: 'UT 항목', options: headerOpts },
      ...versionLabels.map((name) => ({ text: `${name} 총량`, options: headerOpts })),
      { text: '차이 (Δ)', options: headerOpts },
      { text: '증감률', options: headerOpts },
    ],
    ...utTypes.map((ut): PptxGenJS.TableRow => {
      const arr = totals[ut.key];
      const v1 = arr[0];
      const diff = arr[arr.length - 1] - v1;
      const pct = v1 === 0 ? '-' : ((diff / v1) * 100).toFixed(1) + '%';
      const diffColor = diff > 0 ? 'DC2626' : diff < 0 ? '16A34A' : '333333';
      const rowFill = { color: diff !== 0 ? 'FFFBEB' : 'FFFFFF' };
      return [
        { text: ut.label, options: { bold: true, align: 'left', fill: rowFill, fontSize: 10 } },
        ...arr.map((v) => ({
          text: fmt(v),
          options: { align: 'right' as const, fill: rowFill, fontSize: 10 },
        })),
        {
          text: (diff > 0 ? '+' : '') + fmt(diff),
          options: {
            align: 'right',
            fill: rowFill,
            fontSize: 10,
            color: diffColor,
            bold: diff !== 0,
          },
        },
        {
          text: diff === 0 ? '-' : (diff > 0 ? '+' : '') + pct,
          options: { align: 'right', fill: rowFill, fontSize: 10, color: diffColor },
        },
      ];
    }),
  ];

  const labelW = 2.6;
  const restW = (12.73 - labelW) / (n + 2);
  const colW = [labelW, ...Array(n + 2).fill(restW)];

  slide1.addTable(tableData, {
    x: 0.3,
    y: 2.55,
    w: 12.73,
    rowH: 0.42,
    border: { type: 'solid', color: 'E2E8F0', pt: 1 },
    fontSize: 10,
    fontFace: 'Arial',
    colW,
  });

  slide1.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 7.3,
    w: 13.33,
    h: 0.2,
    fill: { color: '00205B' },
  });
  slide1.addText('LG Energy Solution  |  Utility Technology Team  |  Confidential', {
    x: 0.3,
    y: 7.35,
    w: 12.7,
    h: 0.2,
    fontSize: 8,
    color: 'FFFFFF',
    fontFace: 'Arial',
    align: 'center',
  });

  // ══════════════════════════════════════════════
  // Slide 2: 종합 분석 (Comprehensive Analysis)
  // ══════════════════════════════════════════════
  const slide2 = pptx.addSlide();
  slide2.background = { color: 'FFFFFF' };

  slide2.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 0,
    w: 13.33,
    h: 1.1,
    fill: { color: '00205B' },
  });
  slide2.addText('Comprehensive Analysis & Recommendations', {
    x: 0.4,
    y: 0.12,
    w: 10,
    h: 0.5,
    fontSize: 20,
    bold: true,
    color: 'FFFFFF',
    fontFace: 'Arial',
  });
  slide2.addText(`${s1} vs ${s2}  |  상세 분석 및 조치 권고`, {
    x: 0.4,
    y: 0.6,
    w: 10,
    h: 0.35,
    fontSize: 11,
    color: 'A0B4D0',
    fontFace: 'Arial',
  });

  // 6개 분석 항목 (2열 3행)
  const totalMachines = rows.length;
  const qtyChangeMachines = rows.filter((r) => {
    for (const ut of utTypes) {
      if (r.ut[ut.key] && r.ut[ut.key].diffQ !== 0) return true;
    }
    return false;
  }).length;

  const airDiff = (key: 'Return' | 'Exhaust' | 'Supply') => {
    const arr = totals[key];
    return arr[arr.length - 1] - arr[0];
  };

  const analysisItems = [
    {
      title: '① 전체 현황',
      body: `총 ${totalMachines}대 중 ${diffMachines}대(${(
        (diffMachines / totalMachines) *
        100
      ).toFixed(1)}%)에서 UT 사용량 변동 감지. 설계 안정성: ${
        diffMachines < totalMachines * 0.3
          ? '양호'
          : diffMachines < totalMachines * 0.6
          ? '보통'
          : '요개선'
      }`,
      color: '00205B',
    },
    {
      title: '② 주요 증가 항목',
      body:
        incList.length > 0
          ? incList
              .slice(0, 2)
              .map((x) => `${x.ut.key}: +${fmt(x.diff)} (${x.pct?.toFixed(1)}%)`)
              .join('\n') + '\n→ 공급 용량 검토 필요'
          : '사용량 증가 항목 없음',
      color: 'DC2626',
    },
    {
      title: '③ 주요 감소 항목',
      body:
        decList.length > 0
          ? decList
              .slice(0, 2)
              .map((x) => `${x.ut.key}: ${fmt(x.diff)} (${Math.abs(x.pct ?? 0).toFixed(1)}%)`)
              .join('\n') + '\n→ 절감 원인 확인 권장'
          : '사용량 감소 항목 없음',
      color: '16A34A',
    },
    {
      title: `④ Q'ty 변동`,
      body: `Q'ty 변동 Machine: ${qtyChangeMachines}대\n설비 추가/삭제 이력 및 BOM 대조 검토 필요. 총 사용량 차이의 주원인 가능성 높음.`,
      color: 'D97706',
    },
    {
      title: '⑤ Air 계열 분석',
      body: `Return: ${airDiff('Return') >= 0 ? '+' : ''}${fmt(airDiff('Return'))} CMH\nExhaust: ${
        airDiff('Exhaust') >= 0 ? '+' : ''
      }${fmt(airDiff('Exhaust'))} CMH\nSupply: ${airDiff('Supply') >= 0 ? '+' : ''}${fmt(
        airDiff('Supply')
      )} CMH`,
      color: '0EA5E9',
    },
    {
      title: '⑥ 조치 권고',
      body: `① 상위 변동 항목 원인 분석 회의\n② Q'ty 변동 Machine BOM 검토\n③ 사용량 증가 항목 Hook-up 재검토\n④ UT Matrix 업데이트 권장`,
      color: '7C3AED',
    },
  ];

  analysisItems.forEach((item, i) => {
    const col = i % 2;
    const row = Math.floor(i / 2);
    const x = 0.3 + col * 6.55;
    const y = 1.25 + row * 2.0;
    slide2.addShape(pptx.ShapeType.rect, {
      x,
      y,
      w: 6.2,
      h: 1.8,
      fill: { color: 'F8FAFC' },
      line: { color: 'E2E8F0', pt: 1 },
    });
    slide2.addShape(pptx.ShapeType.rect, { x, y, w: 0.05, h: 1.8, fill: { color: item.color } });
    slide2.addText(item.title, {
      x: x + 0.15,
      y: y + 0.1,
      w: 5.9,
      h: 0.35,
      fontSize: 11,
      bold: true,
      color: item.color,
      fontFace: 'Arial',
    });
    slide2.addText(item.body, {
      x: x + 0.15,
      y: y + 0.45,
      w: 5.9,
      h: 1.25,
      fontSize: 9.5,
      color: '334155',
      fontFace: 'Malgun Gothic',
      lineSpacing: 16,
    });
  });

  slide2.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 7.3,
    w: 13.33,
    h: 0.2,
    fill: { color: '00205B' },
  });
  slide2.addText('LG Energy Solution  |  Utility Technology Team  |  Confidential', {
    x: 0.3,
    y: 7.35,
    w: 12.7,
    h: 0.2,
    fontSize: 8,
    color: 'FFFFFF',
    fontFace: 'Arial',
    align: 'center',
  });

  await pptx.writeFile({ fileName: 'UT_Summary_McKinsey_Deck.pptx' });
}
